import React, { useEffect, useState } from 'react';
import { Heart, LogOut, Check, X, Users, Wallet, Clock, BadgeCheck, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { adminPayments, adminStats, adminApprove, adminReject, fileUrl, formatRupiahFull } from '../api';

const TABS = [{ v: 'pending', l: 'Menunggu' }, { v: 'approved', l: 'Disetujui' }, { v: 'rejected', l: 'Ditolak' }, { v: '', l: 'Semua' }];
const STATUS_STYLE = { pending: 'bg-terracotta-100 text-terracotta-600', approved: 'bg-sage-100 text-sage-700', rejected: 'bg-red-50 text-red-500' };

export default function Admin() {
  const { toast } = useToast();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);

  const load = () => { adminPayments(tab).then(setItems).catch(() => {}); adminStats().then(setStats).catch(() => {}); };
  useEffect(load, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (p) => {
    if (!window.confirm(`Setujui pembayaran ${p.user_name} dan aktifkan akunnya?`)) return;
    const r = await adminApprove(p.id); load();
    if (r.waLink) {
      window.open(r.waLink, '_blank');
      toast({ title: 'Akun diaktifkan ✨', description: 'WhatsApp terbuka — tekan kirim untuk memberi kabar ke user.' });
    } else toast({ title: 'Akun diaktifkan ✨', description: `${p.user_email} (tidak ada nomor WA untuk notifikasi)` });
  };
  const reject = async (p) => {
    const reason = window.prompt('Alasan penolakan (opsional):', 'Transfer tidak ditemukan di mutasi rekening');
    if (reason === null) return;
    await adminReject(p.id, reason); load(); toast({ title: 'Konfirmasi ditolak' });
  };

  const fmtDate = (s) => new Date(s).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="min-h-screen bg-cream-100 px-5 py-8" data-testid="admin-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-sage-700 flex items-center justify-center"><Heart className="w-5 h-5 text-white" fill="white" /></div>
            <div><div className="font-serif text-xl text-sage-700 leading-none">Naik Pelaminan</div><div className="text-[10px] text-terracotta-600 tracking-widest mt-1">PANEL ADMIN</div></div>
          </div>
          <div className="flex items-center gap-4 text-sm text-sage-500">
            <span className="hidden sm:inline">{user?.email}</span>
            <button onClick={logout} className="flex items-center gap-1.5 hover:text-terracotta-600" data-testid="admin-logout-button"><LogOut className="w-4 h-4" /> Keluar</button>
          </div>
        </div>

        <h1 className="font-serif text-4xl text-sage-700 mb-6">Verifikasi Pembayaran</h1>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              { l: 'Total user', v: stats.users, I: Users, c: 'text-sage-700' },
              { l: 'Akun aktif', v: stats.paid, I: BadgeCheck, c: 'text-sage-600' },
              { l: 'Menunggu', v: stats.pending, I: Clock, c: 'text-terracotta-500' },
              { l: 'Pendapatan', v: formatRupiahFull(stats.revenue), I: Wallet, c: 'text-sage-700' }
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-4 border border-cream-200" data-testid={`admin-stat-${i}`}>
                <s.I className={`w-4 h-4 ${s.c} mb-2`} />
                <div className={`font-serif text-2xl ${s.c}`}>{s.v}</div>
                <div className="text-xs text-sage-500">{s.l}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4">
          {TABS.map(t => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`admin-tab-${t.v || 'all'}`}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t.v ? 'bg-terracotta-500 text-white' : 'bg-white border border-cream-200 text-sage-600'}`}>{t.l}</button>
          ))}
        </div>

        <div className="space-y-3">
          {items.map(p => (
            <div key={p.id} className="bg-white rounded-2xl p-5 border border-cream-200 flex flex-col md:flex-row md:items-center gap-4" data-testid={`payment-row-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-sage-700">{p.user_name}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[p.status]}`}>{p.status}</span>
                </div>
                <div className="text-sm text-sage-500 truncate">{p.user_email}</div>
                <div className="text-sm text-sage-600 mt-2">Pengirim: <b>{p.senderName}</b> · {p.senderBank} · <span className="text-terracotta-600 font-medium">{formatRupiahFull(p.amount)}</span>{p.phone && <span className="text-sage-500"> · WA {p.phone}</span>}</div>
                <div className="text-xs text-sage-400 mt-1">Dikirim {fmtDate(p.created_at)}{p.verified_at ? ` · Diverifikasi ${fmtDate(p.verified_at)}` : ''}{p.reason ? ` · ${p.reason}` : ''}</div>
                {p.proofFileId ? (
                  <a href={fileUrl(p.proofFileId)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-3 text-xs text-terracotta-600 font-medium" data-testid={`proof-link-${p.id}`}>
                    <img src={fileUrl(p.proofFileId)} alt="Bukti" className="h-16 w-16 rounded-xl object-cover border border-cream-200" /> <span className="flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Lihat bukti transfer</span>
                  </a>
                ) : <div className="text-xs text-sage-400 mt-2 italic">Tanpa bukti transfer</div>}
              </div>
              {p.status === 'pending' && (
                <div className="flex gap-2">
                  <Button onClick={() => approve(p)} className="bg-sage-600 hover:bg-sage-700 text-white rounded-full h-10 px-5" data-testid={`approve-button-${p.id}`}><Check className="w-4 h-4 mr-1.5" /> Setujui</Button>
                  <Button onClick={() => reject(p)} variant="outline" className="rounded-full h-10 px-5 border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600" data-testid={`reject-button-${p.id}`}><X className="w-4 h-4 mr-1.5" /> Tolak</Button>
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && <div className="text-center py-16 text-sage-400 text-sm" data-testid="admin-empty">Tidak ada data</div>}
        </div>
      </div>
    </div>
  );
}
